import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { Filters } from '../../../../hooks/products';
import { useQueryFilterFunction } from '../../../../hooks/products';
import { itemHasCount, itemHasOnlyUnits, type ProductOrderItem } from '../data/product-order-item';
import { matchesIsBuyup, matchesSize } from '../utils';

export type ShowFilter = 'all' | 'ordering' | 'broken';

function matchesShow(orderItem: ProductOrderItem, show: ShowFilter): boolean {
  switch (show) {
    case 'all':
      return true;
    case 'ordering':
      return itemHasCount(orderItem);
    case 'broken':
      return itemHasOnlyUnits(orderItem);
  }
}

function useFilteredOrderItems({
  orderItems,
  query,
  sizes,
  types,
  labelIds,
  maxDoh,
  showFilter,
  isBuyup,
  includeInactiveBuyup,
}: {
  orderItems: ProductOrderItem[];
  query: string;
  sizes: string[];
  types: string[];
  labelIds: number[];
  maxDoh: number | undefined;
  showFilter: ShowFilter;
  isBuyup: boolean;
  includeInactiveBuyup: boolean;
}) {
  const products = useMemo(() => orderItems.map((oi) => oi.product), [orderItems]);
  const matchesProduct = useQueryFilterFunction(query, products);

  return useMemo(() => {
    return orderItems.filter((orderItem) => {
      const { product } = orderItem;

      return (
        matchesProduct(product) &&
        matchesSize(product, sizes) &&
        matchesIsBuyup(product, isBuyup, includeInactiveBuyup) &&
        matchesShow(orderItem, showFilter) &&
        (types.length === 0 || types.includes(product.type)) &&
        (labelIds.length === 0 || labelIds.some((id) => product.label_ids.includes(id))) &&
        (maxDoh === undefined || product.stats.days_on_hand <= maxDoh)
      );
    });
  }, [
    orderItems,
    sizes,
    types,
    labelIds,
    maxDoh,
    showFilter,
    isBuyup,
    includeInactiveBuyup,
    matchesProduct,
  ]);
}

export function useFilteredOrder(
  orderItems: ProductOrderItem[],
  isBuyup: boolean,
  includeInactiveBuyup = false
) {
  const [query, setQuery] = useState('');
  const [sizes, setSizes] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [labelIds, setLabelIds] = useState<number[]>([]);
  const [maxDoh, setMaxDoh] = useState<number | undefined>(undefined);
  const [showFilter, setShowFilter] = useState<ShowFilter>('all');

  const filteredOrderItems = useFilteredOrderItems({
    orderItems,
    query,
    sizes,
    types,
    labelIds,
    maxDoh,
    showFilter,
    isBuyup,
    includeInactiveBuyup,
  });

  const orderProducts = useMemo(() => orderItems.map((oi) => oi.product), [orderItems]);

  const filterContext = useMemo(
    () => ({
      filters: { sizes, types, labelIds, maximumDOH: maxDoh },
      setFilters: (updater: React.SetStateAction<Filters>) => {
        const current: Filters = { sizes, types, labelIds, maximumDOH: maxDoh };
        const next = typeof updater === 'function' ? updater(current) : updater;
        if (next.sizes !== undefined) {
          setSizes(next.sizes);
        }

        if (next.types !== undefined) {
          setTypes(next.types);
        }

        if (next.labelIds !== undefined) {
          setLabelIds(next.labelIds);
        }

        if ('maximumDOH' in next) {
          setMaxDoh(next.maximumDOH);
        }
      },
      filteredProducts: orderProducts,
    }),
    [sizes, types, labelIds, maxDoh, orderProducts]
  );

  // The popover controls a user can move off their defaults. The badge counts
  // these and "Clear all" resets them; the search box is excluded (own count).
  const activeFilterCount =
    (showFilter !== 'all' ? 1 : 0) +
    (sizes.length > 0 ? 1 : 0) +
    (types.length > 0 ? 1 : 0) +
    (labelIds.length > 0 ? 1 : 0) +
    (maxDoh !== undefined ? 1 : 0);

  const clearFilters = useCallback(() => {
    setShowFilter('all');
    setSizes([]);
    setTypes([]);
    setLabelIds([]);
    setMaxDoh(undefined);
  }, []);

  return {
    query,
    setQuery,
    filteredOrderItems,
    filterContext,
    orderProducts,
    showFilter,
    setShowFilter,
    activeFilterCount,
    clearFilters,
  };
}
